import type { PluginWithOptions } from 'markdown-it';
import type StateInline from 'markdown-it/lib/rules_inline/state_inline';
import type StateBlock from 'markdown-it/lib/rules_block/state_block';

import { MathToken } from '@scale/shared/types/config';

const $_pre = (str: string, outerSpace: boolean = false, beg: number) => {
  const prv = beg > 0 ? str[beg - 1].charCodeAt(0) : false;
  return outerSpace
    ? !prv || prv === 0x20 // space  (avoiding regex's for performance reasons)
    : !prv ||
        (prv !== 0x5c && // no backslash,
          (prv < 0x30 || prv > 0x39)); // no decimal digit .. before opening '$'
};
const $_post = (str: string, outerSpace: boolean = false, end: number) => {
  const nxt = str[end + 1] && str[end + 1].charCodeAt(0);
  return outerSpace
    ? !nxt ||
        nxt === 0x20 || // space  (avoiding regex's for performance reasons)
        nxt === 0x2e || // '.'
        nxt === 0x2c || // ','
        nxt === 0x3b // ';'
    : !nxt || nxt < 0x30 || nxt > 0x39; // no decimal digit .. after closing '$'
};

export type RuleName =
  | MathToken.BRACKETS
  | MathToken.DOLLARS
  | 'gitlab'
  | MathToken.ESCAPED_BRACKETS;

type Rule = {
  name: string;
  rex: RegExp;
  tmpl: string;
  tag: string;
  closingTag: string;
  outerSpace?: boolean;
  pre?: typeof $_pre;
  post?: typeof $_post;
  displayMode?: boolean;
};

type RuleSet = {
  inline: Array<Rule>;
  block: Array<Rule>;
};

type RuleSetContainer = Record<RuleName, RuleSet>;

const blockNodeTmpl = `<math-display class="math-node" open="$open" close="$close">$content</math-display>`;
const inlineNodeTmpl = `<math-inline class="math-node" open="$open" close="$close">$content</math-inline>`;

const RULES: RuleSetContainer = {
  [MathToken.BRACKETS]: {
    inline: [
      {
        name: 'math_inline_block_bracket',
        rex: /\\\[([\s\S]+?)\\\]/gy,
        tmpl: blockNodeTmpl,
        tag: '\\[',
        closingTag: '\\]',
        displayMode: true,
        pre: $_pre,
        post: $_post,
      },
      {
        name: 'math_inline_bracket',
        rex: /\\\((.+?)\\\)/gy,
        tmpl: inlineNodeTmpl,
        tag: '\\(',
        closingTag: '\\)',
      },
    ],
    block: [
      {
        name: 'math_block_bracket',
        rex: /\\\[([\s\S]+?)\\\]/gmy,
        tmpl: blockNodeTmpl,
        tag: '\\[',
        closingTag: '\\]',
      },
    ],
  },
  [MathToken.ESCAPED_BRACKETS]: {
    inline: [
      {
        name: 'math_inline_block_escaped_bracket',
        rex: /\\\\\[([\s\S]+?)\\\\\]/gy,
        tmpl: blockNodeTmpl,
        tag: '\\\\[',
        closingTag: '\\\\]',
        displayMode: true,
        pre: $_pre,
        post: $_post,
      },
      {
        name: 'math_inline_escaped_bracket',
        rex: /\\\\\((.+?)\\\\\)/gy,
        tmpl: inlineNodeTmpl,
        tag: '\\\\(',
        closingTag: '\\\\)',
      },
    ],
    block: [
      {
        name: 'math_block_escaped_bracket',
        rex: /\\\\\[([\s\S]+?)\\\\\]/gmy,
        tmpl: blockNodeTmpl,
        tag: '\\\\[',
        closingTag: '\\\\]',
      },
    ],
  },
  [MathToken.DOLLARS]: {
    inline: [
      {
        name: 'math_inline_double_dollars',
        rex: /\${2}([^$]*?[^\\])\${2}/gy,
        tmpl: blockNodeTmpl,
        tag: '$$',
        closingTag: '$$',
        displayMode: true,
        pre: $_pre,
        post: $_post,
      },
      {
        name: 'math_inline_dollars',
        // /(?:^|\s)(\$(?!\s+\$)((?:[^$]+))\$(?!\s+\$))$/
        rex: /\$((?:[^\s\\])|(?:\S.*?[^\s\\]))\$/gy,
        // rex: /(?:^|\s)(\$(?!\s+\$)((?:[^$]+))\$)(?=\s|$)/gy,
        tmpl: inlineNodeTmpl,
        tag: '$',
        closingTag: '$',
        outerSpace: false,
        pre: $_pre,
        post: $_post,
      },
    ],
    block: [
      {
        name: 'math_block_dollars',
        rex: /\${2}([^$]*?[^\\])\${2}/gmy,
        tmpl: blockNodeTmpl,
        tag: '$$',
        closingTag: '$$',
      },
    ],
  },
  gitlab: {
    inline: [
      {
        name: 'math_inline_gitlab',
        rex: /\$`(.+?)`\$/gy,
        tmpl: inlineNodeTmpl,
        tag: '$`',
        closingTag: '`$',
      },
    ],
    block: [
      {
        name: 'math_block_gitlab',
        rex: /`{3}math\s*([^`]*?)\s*`{3}/gm,
        tmpl: blockNodeTmpl,
        tag: '```math',
        closingTag: '```',
      },
    ],
  },
} as const;

const mergeDelimiters = function (delims: RuleName[] = [MathToken.DOLLARS]) {
  const delimiters: { inline: Rule[]; block: Rule[] } = { inline: [], block: [] }; // target of merge process ...

  for (const delim of delims) // merge them into delimiters ...
    if (delim in RULES) {
      delimiters.inline.push(...RULES[delim].inline);
      delimiters.block.push(...RULES[delim].block);
    }

  return delimiters;
};

export type TexMathOptions = {
  delimiters?: RuleName[];
  outerSpace?: boolean;
  rules: 'all' | 'inline' | 'block';
};

function ensureReplace(replace: string) {
  return replace === '$$' ? '$$$$' : replace;
}

const texmath: PluginWithOptions<TexMathOptions> = function (md, options) {
  // @ts-ignore
  // this.inlineRuleNames = ['math_inline', 'math_inline_double'];
  // @ts-ignore
  // this.blockRuleNames = ['math_block'];

  const delimiters = mergeDelimiters(options?.delimiters);
  const outerSpace = options?.outerSpace ?? false; // inline rules, effectively `dollars` require surrounding spaces, i.e ` $\psi$ `, to be accepted as inline formulas. This is primarily a guard against misinterpreting single `$`'s in normal markdown text (relevant for inline math only. Default: `false`, for backwards compatibility).
  const rules = options?.rules ?? 'all';

  if (rules === 'all' || rules === 'inline') {
    // inject inline rules to markdown-it
    for (const rule of delimiters.inline) {
      if (!!outerSpace && 'outerSpace' in rule) rule.outerSpace = true;

      md.inline.ruler.before('escape', rule.name, inlineRule(rule)); // ! important

      md.renderer.rules[rule.name] = (tokens, idx) => {
        return rule.tmpl
          .replace(/\$content/, tokens[idx].content)
          .replace(/\$open/, rule.tag)
          .replace(/\$close/, rule.closingTag);
      };
    }
  }

  if (rules === 'all' || rules === 'block') {
    // inject block rules to markdown-it
    for (const rule of delimiters.block) {
      md.block.ruler.before('fence', rule.name, blockRule(rule)); // ! important for ```math delimiters
      md.renderer.rules[rule.name] = (tokens, idx) => {
        return rule.tmpl
          .replace(/\$content/, tokens[idx].content)
          .replace(/\$open/, ensureReplace(rule.tag))
          .replace(/\$close/, ensureReplace(rule.closingTag));
      };
    }
  }
};

const inlineRule = (rule: Rule) =>
  function (state: StateInline, silent: boolean) {
    const pos = state.pos;
    const str = state.src;
    const pre =
      str.startsWith(rule.tag, (rule.rex.lastIndex = pos)) &&
      (!rule.pre || rule.pre(str, rule?.outerSpace, pos)); // valid pre-condition ...
    const match = pre && rule.rex.exec(str);
    const res =
      !!match &&
      pos < rule.rex.lastIndex &&
      (!rule.post || rule.post(str, rule?.outerSpace, rule.rex.lastIndex - 1));

    if (res) {
      if (!silent) {
        const token = state.push(rule.name, 'math', 0);
        token.content = match[1];
        token.markup = rule.tag;
      }
      state.pos = rule.rex.lastIndex;
    }
    return res;
  };

const blockRule = (rule: Rule) =>
  function block(state: StateBlock, begLine: number, endLine: number, silent: boolean) {
    const pos = state.bMarks[begLine] + state.tShift[begLine];
    const str = state.src;
    const pre =
      str.startsWith(rule.tag, (rule.rex.lastIndex = pos)) &&
      (!rule.pre || rule.pre(str, false, pos)); // valid pre-condition ....
    const match = pre && rule.rex.exec(str);
    const res =
      !!match &&
      pos < rule.rex.lastIndex &&
      (!rule.post || rule.post(str, false, rule.rex.lastIndex - 1));

    if (res && !silent) {
      // match and valid post-condition ...
      const endpos = rule.rex.lastIndex - 1;
      let curline;

      for (curline = begLine; curline < endLine; curline++)
        if (
          endpos >= state.bMarks[curline] + state.tShift[curline] &&
          endpos <= state.eMarks[curline]
        )
          // line for end of block math found ...
          break;

      // "this will prevent lazy continuations from ever going past our end marker"
      // s. https://github.com/markdown-it/markdown-it-container/blob/master/index.js
      const lineMax = state.lineMax;
      const parentType = state.parentType;
      state.lineMax = curline;
      // @ts-ignore
      state.parentType = 'math';

      if (parentType === 'blockquote')
        // remove all leading '>' inside multiline formula
        match[1] = match[1].replace(/(\n*?^(?:\s*>)+)/gm, '');
      // begin token
      const token = state.push(rule.name, 'math', 0); // 'math_block'
      token.block = true;
      token.tag = rule.tag;
      token.markup = '';
      token.content = match[1];
      token.info = match[match.length - 1]; // eq.no
      token.map = [begLine, curline + 1];
      //            token.hidden = true;
      // end token ... superfluous ...

      state.parentType = parentType;
      state.lineMax = lineMax;
      state.line = curline + 1;
    }
    return res;
  };

// used for enable/disable math rendering by `markdown-it`

export default texmath;
